import { Api as api } from "../../api";

const prefix = "/transport-resource";

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    fleetServiceV1GetFleets: build.query<
      FleetServiceV1GetFleetsApiResponse,
      FleetServiceV1GetFleetsApiArg
    >({
      query: (queryArg) => ({
        url: `${prefix}/v1/fleets`,
        params: {
          page: queryArg.page,
          limit: queryArg.limit,
          search: queryArg.search,
          status: queryArg.status,
          sort_by: queryArg.sortBy,
          order: queryArg.order,
          vendor_id: queryArg.vendorId,
          vehicle_type_id: queryArg.vehicleTypeId,
          vehicle_model_id: queryArg.vehicleModelId,
        },
      }),
      providesTags: ["Fleet"],
    }),
    fleetServiceV1CreateFleet: build.mutation<
      FleetServiceV1CreateFleetApiResponse,
      FleetServiceV1CreateFleetApiArg
    >({
      query: (queryArg) => ({
        url: `${prefix}/v1/fleets`,
        method: "POST",
        body: queryArg.transportResourceCreateFleetRequest,
      }),
      invalidatesTags: ["Fleet"],
    }),
    fleetServiceV1BulkDeleteFleets: build.mutation<
      FleetServiceV1BulkDeleteFleetsApiResponse,
      FleetServiceV1BulkDeleteFleetsApiArg
    >({
      query: (queryArg) => ({
        url: `${prefix}/v1/fleets/bulk-delete`,
        method: "POST",
        body: queryArg.transportResourceBulkDeleteFleetsReq,
      }),
      invalidatesTags: ["Fleet"],
    }),
    fleetServiceV1BulkUpdateFleetStatus: build.mutation<
      FleetServiceV1BulkUpdateFleetStatusApiResponse,
      FleetServiceV1BulkUpdateFleetStatusApiArg
    >({
      query: (queryArg) => ({
        url: `${prefix}/v1/fleets/bulk-status`,
        method: "PATCH",
        body: queryArg.transportResourceBulkUpdateFleetStatusReq,
      }),
      invalidatesTags: ["Fleet"],
    }),
    fleetServiceV1CreateFleetDraft: build.mutation<
      FleetServiceV1CreateFleetDraftApiResponse,
      FleetServiceV1CreateFleetDraftApiArg
    >({
      query: (queryArg) => ({
        url: `${prefix}/v1/fleets/draft`,
        method: "POST",
        body: queryArg.transportResourceCreateFleetDraftRequest,
      }),
      invalidatesTags: ["Fleet"],
    }),
    fleetServiceV1UpdateFleetDraft: build.mutation<
      FleetServiceV1UpdateFleetDraftApiResponse,
      FleetServiceV1UpdateFleetDraftApiArg
    >({
      query: (queryArg) => ({
        url: `${prefix}/v1/fleets/draft/${queryArg.id}`,
        method: "PUT",
        body: queryArg.fleetServiceV1UpdateFleetDraftBody,
      }),
      invalidatesTags: ["Fleet"],
    }),
    fleetServiceV1ExportFleets: build.mutation<
      Blob,
      FleetServiceV1ExportFleetsApiArg
    >({
      query: (queryArg) => ({
        url: `${prefix}/v1/fleets/export`,
        method: "POST",
        body: queryArg.transportResourceExportFleetsRequest,
        responseHandler: (response) =>
          response.ok ? response.blob() : response.json(),
      }),
    }),
    fleetServiceV1ImportFleets: build.mutation<
      TransportResourceImportFleetRes,
      { apiHttpBody: FormData; lang?: string }
    >({
      query: ({ apiHttpBody, lang }) => ({
        url: `${prefix}/v1/fleets/import`,
        method: "POST",
        body: apiHttpBody,
        params: { lang },
      }),
      invalidatesTags: ["Fleet"],
    }),
    fleetServiceV1DownloadFleetImportTemplate: build.query<
      FleetServiceV1DownloadFleetImportTemplateApiResponse,
      FleetServiceV1DownloadFleetImportTemplateApiArg
    >({
      query: () => ({ url: `${prefix}/v1/fleets/import/template` }),
    }),
    fleetServiceV1GetFleet: build.query<
      FleetServiceV1GetFleetApiResponse,
      FleetServiceV1GetFleetApiArg
    >({
      query: (queryArg) => ({ url: `${prefix}/v1/fleets/${queryArg.id}` }),
      providesTags: ["Fleet"],
    }),
    fleetServiceV1UpdateFleet: build.mutation<
      FleetServiceV1UpdateFleetApiResponse,
      FleetServiceV1UpdateFleetApiArg
    >({
      query: (queryArg) => ({
        url: `${prefix}/v1/fleets/${queryArg.id}`,
        method: "PUT",
        body: queryArg.fleetServiceV1UpdateFleetBody,
      }),
      invalidatesTags: ["Fleet"],
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as fleetApi };
export type FleetServiceV1GetFleetsApiResponse =
  /** status 200 A successful response. */ TransportResourceGetFleetsRes;
export type FleetServiceV1GetFleetsApiArg = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  order?: string;
  vendorId?: string;
  vehicleTypeId?: string;
  vehicleModelId?: string;
};
export type FleetServiceV1CreateFleetApiResponse =
  /** status 200 A successful response. */ TransportResourceCreateFleetResponse;
export type FleetServiceV1CreateFleetApiArg = {
  transportResourceCreateFleetRequest: TransportResourceCreateFleetRequest;
};
export type FleetServiceV1BulkDeleteFleetsApiResponse =
  /** status 200 A successful response. */ TransportResourceBulkDeleteFleetsRes;
export type FleetServiceV1BulkDeleteFleetsApiArg = {
  transportResourceBulkDeleteFleetsReq: TransportResourceBulkDeleteFleetsReq;
};
export type FleetServiceV1BulkUpdateFleetStatusApiResponse =
  /** status 200 A successful response. */ TransportResourceBulkUpdateFleetStatusRes;
export type FleetServiceV1BulkUpdateFleetStatusApiArg = {
  transportResourceBulkUpdateFleetStatusReq: TransportResourceBulkUpdateFleetStatusReq;
};
export type FleetServiceV1CreateFleetDraftApiResponse =
  /** status 200 A successful response. */ TransportResourceCreateFleetResponse;
export type FleetServiceV1CreateFleetDraftApiArg = {
  transportResourceCreateFleetDraftRequest: TransportResourceCreateFleetDraftRequest;
};
export type FleetServiceV1UpdateFleetDraftApiResponse =
  /** status 200 A successful response. */ TransportResourceUpdateFleetResponse;
export type FleetServiceV1UpdateFleetDraftApiArg = {
  id: string;
  fleetServiceV1UpdateFleetDraftBody: FleetServiceV1UpdateFleetDraftBody;
};
export type FleetServiceV1ExportFleetsApiResponse = Blob;
export type FleetServiceV1ExportFleetsApiArg = {
  transportResourceExportFleetsRequest: TransportResourceExportFleetsRequest;
};
export type FleetServiceV1ImportFleetsApiResponse =
  /** status 200 Fleet imported successfully */ TransportResourceImportFleetRes;
export type FleetServiceV1ImportFleetsApiArg = {
  apiHttpBody: FormData;
  lang?: string;
};
export type FleetServiceV1DownloadFleetImportTemplateApiResponse = unknown;
export type FleetServiceV1DownloadFleetImportTemplateApiArg = void;
export type FleetServiceV1GetFleetApiResponse =
  /** status 200 A successful response. */ TransportResourceGetFleetRes;
export type FleetServiceV1GetFleetApiArg = {
  id: string;
};
export type FleetServiceV1UpdateFleetApiResponse =
  /** status 200 A successful response. */ TransportResourceUpdateFleetResponse;
export type FleetServiceV1UpdateFleetApiArg = {
  id: string;
  fleetServiceV1UpdateFleetBody: FleetServiceV1UpdateFleetBody;
};
export type TransportResourceFleetListRes = {
  id?: string;
  plate_number?: string;
  photo_url?: string;
  vehicle_type?: string;
  vehicle_model?: string;
  odometer?: number;
  status?: string;
  stnk_expiry?: string;
  kir_expiry?: string;
  last_maintenance_date?: string;
  vendor_name?: string;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  driver_id?: string;
  driver_name?: string;
};
export type TransportResourceFleetsListMeta = {
  total?: number;
  current_page?: number;
  limit?: number;
  total_item?: number;
  total_page?: number;
  have_next_page?: boolean;
  have_previous_page?: boolean;
};
export type TransportResourceGetFleetsRes = {
  fleet?: TransportResourceFleetListRes[];
  metadata?: TransportResourceFleetsListMeta;
};
export type ProtobufAny = {
  /** Identifies the type of the serialized Protobuf message with a URI reference
    consisting of a prefix ending in a slash and the fully-qualified type name.
    
    Example: type.googleapis.com/google.protobuf.StringValue
    
    This string must contain at least one `/` character, and the content after
    the last `/` must be the fully-qualified name of the type in canonical
    form, without a leading dot. Do not write a scheme on these URI references
    so that clients do not attempt to contact them.
    
    The prefix is arbitrary and Protobuf implementations are expected to
    simply strip off everything up to and including the last `/` to identify
    the type. `type.googleapis.com/` is a common default prefix that some
    legacy implementations require. This prefix does not indicate the origin of
    the type, and URIs containing it are not expected to respond to any
    requests.
    
    All type URL strings must be legal URI references with the additional
    restriction (for the text format) that the content of the reference
    must consist only of alphanumeric characters, percent-encoded escapes, and
    characters in the following set (not including the outer backticks):
    `/-.~_!$&()*+,;=`. Despite our allowing percent encodings, implementations
    should not unescape them to prevent confusion with existing parsers. For
    example, `type.googleapis.com%2FFoo` should be rejected.
    
    In the original design of `Any`, the possibility of launching a type
    resolution service at these type URLs was considered but Protobuf never
    implemented one and considers contacting these URLs to be problematic and
    a potential security issue. Do not attempt to contact type URLs. */
  "@type"?: string;
  [key: string]: any;
};
export type RpcStatus = {
  code?: number;
  message?: string;
  details?: ProtobufAny[];
};
export type TransportResourceCreateFleetResponse = {
  message?: string;
  success?: boolean;
};
export type TransportResourceCreateFleetDocumentInput = {
  document_type?: string;
  file_url?: string;
};
export type TransportResourceCreateFleetRequest = {
  plate_number: string;
  hull_number: string;
  vehicle_type_id: string;
  vehicle_model_id: string;
  associated_auxiliary_id?: string[];
  vendor_id: string;
  odometer: number;
  stnk_expiry_date: string;
  kir_expiry_date: string;
  fleet_picture_url?: string;
  last_maintenance_date?: string;
  documents?: TransportResourceCreateFleetDocumentInput[];
  driver_id?: string;
};
export type TransportResourceBulkDeleteFleetsRes = {
  message?: string;
  success?: boolean;
};
export type TransportResourceBulkDeleteFleetsReq = {
  ids?: string[];
};
export type TransportResourceBulkUpdateFleetStatusRes = {
  message?: string;
  success?: boolean;
};
export type TransportResourceBulkUpdateFleetStatusReq = {
  ids?: string[];
  status: string;
};
export type TransportResourceCreateFleetDraftRequest = {
  plate_number?: string;
  hull_number?: string;
  vehicle_type_id?: string;
  vehicle_model_id?: string;
  associated_auxiliary_id?: string[];
  vendor_id?: string;
  odometer?: number;
  stnk_expiry_date?: string;
  kir_expiry_date?: string;
  fleet_picture_url?: string;
  last_maintenance_date?: string;
  documents?: TransportResourceCreateFleetDocumentInput[];
  driver_id?: string;
};
export type TransportResourceUpdateFleetResponse = {
  message?: string;
  success?: boolean;
};
export type FleetServiceV1UpdateFleetDraftBody = {
  plate_number?: string;
  hull_number?: string;
  vehicle_type_id?: string;
  vehicle_model_id?: string;
  associated_auxiliary_id?: string[];
  vendor_id?: string;
  odometer?: number;
  stnk_expiry_date?: string;
  kir_expiry_date?: string;
  fleet_picture_url?: string;
  last_maintenance_date?: string;
  documents?: TransportResourceCreateFleetDocumentInput[];
  driver_id?: string;
};
export type TransportResourceGetFleetsReq = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sort_by?: string;
  order?: string;
  vendor_id?: string;
  vehicle_type_id?: string;
  vehicle_model_id?: string;
};
export type TransportResourceExportFleetsRequest = {
  format?: string;
  scope?: string;
  ids?: string[];
  filters?: TransportResourceGetFleetsReq;
};
export type TransportResourceFleetImportRowError = {
  row?: number;
  field?: string;
  message?: string;
};
export type TransportResourceImportFleetRes = {
  success?: boolean;
  total_count?: number;
  imported_count?: number;
  failed_count?: number;
  message?: string;
  error_file_url?: string;
  errors?: TransportResourceFleetImportRowError[];
};
export type ApiHttpBody = {
  /** The HTTP Content-Type header value specifying the content type of the body. */
  contentType?: string;
  /** The HTTP request/response body as raw binary. */
  data?: string;
  /** Application specific response metadata. Must be set in the first response
    for streaming APIs. */
  extensions?: ProtobufAny[];
};
export type TransportResourceFleetAuxiliary = {
  id?: string;
  name?: string;
};
export type TransportResourceFleetRes = {
  id?: string;
  plate_number?: string;
  photo_url?: string;
  vehicle_type?: string;
  vehicle_model?: string;
  odometer?: number;
  status?: string;
  stnk_expiry?: string;
  kir_expiry?: string;
  last_maintenance_date?: string;
  vendor_name?: string;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  hull_number?: string;
  vendor_id?: string;
  vehicle_type_id?: string;
  vehicle_model_id?: string;
  documents?: TransportResourceCreateFleetDocumentInput[];
  associated_auxiliaries?: TransportResourceFleetAuxiliary[];
  driver_id?: string;
  driver_name?: string;
};
export type TransportResourceGetFleetRes = {
  fleet?: TransportResourceFleetRes;
};
export type FleetServiceV1UpdateFleetBody = {
  plate_number: string;
  hull_number: string;
  vehicle_type_id: string;
  vehicle_model_id: string;
  associated_auxiliary_id?: string[];
  vendor_id: string;
  odometer: number;
  stnk_expiry_date: string;
  kir_expiry_date: string;
  fleet_picture_url?: string;
  last_maintenance_date?: string;
  documents?: TransportResourceCreateFleetDocumentInput[];
  status: string;
  driver_id?: string;
};
export const {
  useFleetServiceV1GetFleetsQuery,
  useFleetServiceV1CreateFleetMutation,
  useFleetServiceV1BulkDeleteFleetsMutation,
  useFleetServiceV1BulkUpdateFleetStatusMutation,
  useFleetServiceV1CreateFleetDraftMutation,
  useFleetServiceV1UpdateFleetDraftMutation,
  useFleetServiceV1ExportFleetsMutation,
  useFleetServiceV1ImportFleetsMutation,
  useFleetServiceV1DownloadFleetImportTemplateQuery,
  useFleetServiceV1GetFleetQuery,
  useFleetServiceV1UpdateFleetMutation,
} = injectedRtkApi;
